import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, X } from "lucide-react";
import DatePicker from "../../../../Common/DatePicker";
import {
  DsBadge,
  DsButton,
  DsIcon,
  DsInput,
  DsLabel,
} from "../../../../../design-system";
import InfoHint from "./InfoHint";
import HolidayBankAssignModal from "./HolidayBankAssignModal";
import {
  formatHolidayTypeLabel,
  holidayTypeTone,
  holidayYear,
} from "../../../../../lib/holidayBankUtils";

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-1 flex items-center gap-1.5">
      <DsLabel>{children}</DsLabel>
      {hint ? <InfoHint title={hint} /> : null}
    </div>
  );
}

function selectCls() {
  return "w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))]";
}

/**
 * Simple Holiday Policy form:
 * 1) Policy details + RH cap
 * 2) Assigned holidays (year-wise) with Remove — open Bank popup to assign
 * 3) Assignment
 */
export default function HolidayPolicyFormModal({
  open,
  darkMode,
  editingPolicy = null,
  form,
  setForm,
  holidayBank = [],
  branchesList = [],
  departmentsList = [],
  designationsList = [],
  gradesList = [],
  categoriesList = [],
  subDepartmentsList = [],
  employmentTypes = [],
  formatDate,
  saving = false,
  onClose,
  onSubmit,
  onOpenAddBank,
  onEditBank,
  onDeleteBank,
  onSelectAllEmployees,
}) {
  const [bankOpen, setBankOpen] = useState(false);

  const selectedByYear = useMemo(() => {
    const map = new Map();
    (form.holidays || []).forEach((h) => {
      const y = holidayYear(h.date) ?? "Unknown";
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(h);
    });
    for (const list of map.values()) {
      list.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
    }
    return Array.from(map.entries()).sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [form.holidays]);

  if (!open) return null;

  const removeFromPolicy = (holiday) => {
    setForm((prev) => ({
      ...prev,
      holidays: (prev.holidays || []).filter(
        (h) => String(h.id) !== String(holiday.id)
      ),
    }));
  };

  const toggleAssign = (holiday) => {
    setForm((prev) => {
      const exists = (prev.holidays || []).some(
        (h) => String(h.id) === String(holiday.id)
      );
      return {
        ...prev,
        holidays: exists
          ? prev.holidays.filter((h) => String(h.id) !== String(holiday.id))
          : [...(prev.holidays || []), holiday],
      };
    });
  };

  const setAssigned = (key, value) => {
    setForm((prev) => ({
      ...prev,
      assignedTo: {
        ...prev.assignedTo,
        [key]: value ? [value] : [],
      },
    }));
  };

  const rhCount = (form.holidays || []).filter(
    (h) => String(h.type || "").toLowerCase() === "restricted"
  ).length;

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/55 p-3"
          role="presentation"
          onClick={onClose}
          onMouseDown={(e) => {
            // Capture so background ribbons cannot receive clicks
            if (e.target === e.currentTarget) e.preventDefault();
          }}
        >
          <div
            className="flex h-[min(90vh,820px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="holiday-policy-modal-title"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
              <div>
                <h2 id="holiday-policy-modal-title" className="ds-h3">
                  {editingPolicy ? "Edit holiday policy" : "Create holiday policy"}
                </h2>
                <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
                  Name the policy → assign holidays from the bank → set who gets it.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                aria-label="Close"
              >
                <DsIcon icon={X} size="md" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-4">
                {/* 1. Details */}
                <section className="rounded-2xl border border-[hsl(var(--ds-border))] p-4">
                  <h3 className="ds-h4 mb-3">1. Policy details</h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <FieldLabel hint="Unique name for this holiday calendar.">
                        Policy name *
                      </FieldLabel>
                      <DsInput
                        value={form.name}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, name: e.target.value }))
                        }
                        placeholder="e.g. Corporate 2026"
                        darkMode={darkMode}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <FieldLabel hint="From this date the calendar applies.">
                        Effective date *
                      </FieldLabel>
                      <DatePicker
                        value={form.effectiveDate}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            effectiveDate: e.target.value,
                          }))
                        }
                        label="Effective Date"
                        required
                        portal
                        className={selectCls()}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <FieldLabel hint="Internal HR notes only.">
                        Description
                      </FieldLabel>
                      <textarea
                        value={form.description || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            description: e.target.value,
                          }))
                        }
                        rows={2}
                        className={selectCls()}
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <FieldLabel hint="Max Restricted Holiday days per employee per year. Blank = no cap.">
                        Max RH / year
                      </FieldLabel>
                      <DsInput
                        type="number"
                        min={0}
                        value={form.maxRestrictedHolidaysPerYear ?? ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            maxRestrictedHolidaysPerYear:
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                          }))
                        }
                        placeholder="e.g. 2"
                        darkMode={darkMode}
                        className="rounded-xl"
                      />
                      {rhCount > 0 ? (
                        <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
                          {rhCount} RH date{rhCount === 1 ? "" : "s"} assigned
                        </p>
                      ) : null}
                    </div>
                  </div>
                </section>

                {/* 2. Assigned holidays */}
                <section className="rounded-2xl border border-[hsl(var(--ds-border))] p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <h3 className="ds-h4">
                        2. Holidays on this policy ({(form.holidays || []).length})
                      </h3>
                      <InfoHint title="Only these holidays apply under this policy. Open Holiday Bank to tick Assign, or remove here." />
                    </div>
                    <DsButton
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => setBankOpen(true)}
                    >
                      <DsIcon icon={Plus} size="sm" />
                      Choose from Holiday Bank
                    </DsButton>
                  </div>

                  {(form.holidays || []).length === 0 ? (
                    <p className="ds-caption rounded-xl border border-dashed border-[hsl(var(--ds-border))] p-6 text-center text-[hsl(var(--ds-muted-fg))]">
                      No holidays assigned yet. Open Holiday Bank and tick Assign.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {selectedByYear.map(([year, list]) => (
                        <div key={`pol-${year}`}>
                          <p className="ds-caption mb-1.5 font-semibold text-[hsl(var(--ds-muted-fg))]">
                            {year} · {list.length}
                          </p>
                          <ul className="divide-y divide-[hsl(var(--ds-border))] overflow-hidden rounded-xl border border-[hsl(var(--ds-border))]">
                            {list.map((h) => (
                              <li
                                key={h.id}
                                className="flex items-center justify-between gap-3 px-3 py-2"
                              >
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="truncate text-sm font-medium">
                                      {h.name}
                                    </span>
                                    <DsBadge tone={holidayTypeTone(h.type)}>
                                      {formatHolidayTypeLabel(h.type)}
                                    </DsBadge>
                                  </div>
                                  <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                                    {formatDate(h.date)}
                                  </p>
                                </div>
                                <DsButton
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeFromPolicy(h)}
                                >
                                  Remove
                                </DsButton>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* 3. Assignment */}
                <section className="rounded-2xl border border-[hsl(var(--ds-border))] p-4">
                  <div className="mb-3 flex items-center gap-1.5">
                    <h3 className="ds-h4">3. Assignment</h3>
                    <InfoHint title="Who receives this holiday calendar." />
                  </div>
                  <label className="mb-3 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.assignedTo?.allEmployees}
                      onChange={onSelectAllEmployees}
                      className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))]"
                    />
                    <span>Assign to all employees</span>
                  </label>

                  {!form.assignedTo?.allEmployees ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        { key: "branches", label: "Branch", list: branchesList },
                        {
                          key: "departments",
                          label: "Department",
                          list: departmentsList,
                        },
                        {
                          key: "designations",
                          label: "Designation",
                          list: designationsList,
                        },
                        { key: "grades", label: "Grade", list: gradesList },
                        {
                          key: "categories",
                          label: "Category",
                          list: categoriesList,
                        },
                        {
                          key: "subDepartments",
                          label: "Sub-department",
                          list: subDepartmentsList,
                        },
                      ].map((field) => (
                        <div key={field.key}>
                          <FieldLabel>{field.label}</FieldLabel>
                          <select
                            value={form.assignedTo?.[field.key]?.[0] ?? ""}
                            onChange={(e) =>
                              setAssigned(field.key, e.target.value)
                            }
                            className={selectCls()}
                          >
                            <option value="">All</option>
                            {(field.list || []).map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                      <div>
                        <FieldLabel>Employment type</FieldLabel>
                        <select
                          value={form.assignedTo?.employmentTypes?.[0] ?? ""}
                          onChange={(e) =>
                            setAssigned("employmentTypes", e.target.value)
                          }
                          className={selectCls()}
                        >
                          <option value="">All</option>
                          {(employmentTypes || []).map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <FieldLabel hint="Optional: comma-separated employee IDs.">
                          Individual employees
                        </FieldLabel>
                        <DsInput
                          value={(form.assignedTo?.employees || []).join(", ")}
                          onChange={(e) => {
                            const ids = e.target.value
                              .split(",")
                              .map((v) => v.trim())
                              .filter(Boolean);
                            setForm((prev) => ({
                              ...prev,
                              assignedTo: {
                                ...prev.assignedTo,
                                employees: ids,
                              },
                            }));
                          }}
                          placeholder="e.g. 101, 205"
                          darkMode={darkMode}
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                  ) : null}
                </section>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
              <DsButton type="button" variant="outline" size="sm" onClick={onClose}>
                Cancel
              </DsButton>
              <DsButton
                type="button"
                variant="primary"
                size="sm"
                disabled={saving}
                onClick={onSubmit}
              >
                {saving
                  ? editingPolicy
                    ? "Updating…"
                    : "Creating…"
                  : editingPolicy
                    ? "Update policy"
                    : "Create policy"}
              </DsButton>
            </div>
          </div>
        </div>,
        document.body
      )}

      <HolidayBankAssignModal
        open={bankOpen}
        darkMode={darkMode}
        holidayBank={holidayBank}
        selectedHolidays={form.holidays || []}
        formatDate={formatDate}
        onClose={() => setBankOpen(false)}
        onToggleAssign={toggleAssign}
        onAdd={onOpenAddBank}
        onEdit={onEditBank}
        onDelete={onDeleteBank}
      />
    </>
  );
}
